import React, { useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import './App.css'; // Styles will be updated below
import { QRCodeSVG } from 'qrcode.react';  // Add this import at the top

// --- Configuration ---
// Use environment variable, fallback to localhost for safety/simplicity if not set during local dev start
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://dev.qupplo.se';
console.log("Connecting to Socket.IO Server at:", SOCKET_URL); // Good for debugging
const socket = io(SOCKET_URL);

const DEBUG_MODE = process.env.NODE_ENV === 'development';

function App() {
  // --- State Variables ---
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [view, setView] = useState('home'); // 'home', 'lobby', 'question', 'reveal', 'end'
  const [role, setRole] = useState(null); // 'host', 'player', 'display'
  const [username, setUsername] = useState('');
  const [inputRoomCode, setInputRoomCode] = useState('');
  const [roomCode, setRoomCode] = useState(null);
  const [isHost, setIsHost] = useState(false); // Still useful to know who created it
  const [lobbyPlayers, setLobbyPlayers] = useState([]);
  const [hostUsername, setHostUsername] = useState(''); // Store host's name separately for display
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [playerAnswerResult, setPlayerAnswerResult] = useState(null);
  const [displayRevealData, setDisplayRevealData] = useState(null);
  const [finalLeaderboard, setFinalLeaderboard] = useState(null);
  const [error, setError] = useState(null);
  const [submittedAnswerIndex, setSubmittedAnswerIndex] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [answerCount, setAnswerCount] = useState({ count: 0, total: 0 }); // For display

  // Updated State
  const [birthYear, setBirthYear] = useState('1990'); // Default birth year for slider
  const [location, setLocation] = useState('Sweden'); // Default location
  const [currentAttemptId, setCurrentAttemptId] = useState(null);
  const [ratingInput, setRatingInput] = useState('5'); // Default rating for slider
  const [submittedRating, setSubmittedRating] = useState(null);
  const [questionLimit, setQuestionLimit] = useState(10); // NEW: Question limit state
  const [hostAsPlayer, setHostAsPlayer] = useState(true); // NEW: Host mode toggle

  // --- Add state for available sets and selected set ---
  const [availableSets, setAvailableSets] = useState([]); // List of sets from backend
  const [selectedSetId, setSelectedSetId] = useState(''); // Set ID selected by host

  const DUMMY_DATA = {
    question: {
      index: 2,
      totalQuestions: 10,
      text: "What is the capital of Sweden?",
      alts: [
        "Stockholm, the vibrant capital known for its archipelago and historic Gamla Stan",
        "Oslo",
        "Copenhagen, famous for Tivoli Gardens, Nyhavn harbor, and the Little Mermaid statue",
        "Helsinki, a city blending modern design with neoclassical architecture and numerous islands",
        "Berlin, a major center of politics, culture, media, and science with a complex history",
        "Paris, the 'City of Light', renowned for its art, fashion, gastronomy, and landmarks like the Eiffel Tower"
      ],
      num_alternatives: 6,
      time_limit: 15
    },
    playerResult: {
      correct: true,
      score_added: 100,
      total_score: 250
    },
    displayData: {
      correct_index: 0,
      explanation: "Stockholm is the capital and largest city of Sweden.",
      stats: [3, 2, 1, 1, 0, 0],
      leaderboard: [
        { username: "Player1", score: 300 },
        { username: "Player2", score: 250 },
        { username: "Player3", score: 200 }
      ]
    },
    finalLeaderboard: [
      { username: "Player1", score: 500 },
      { username: "Player2", score: 400 },
      { username: "Player3", score: 300 }
    ]
  };

  const timerIntervalRef = useRef(null);

  // Define location options
  const locationOptions = [
      "Sweden", "Norway", "Denmark", "Finland", "Iceland",
      "Germany", "UK", "France", "Spain", "Italy",
      "USA", "Canada", "Australia", "Other Europe", "Other North America",
      "South America", "Asia", "Africa", "Other"
  ];

  // --- Utility Functions ---
  const clearError = () => setError(null);
  const resetGameState = useCallback((backToHome = false) => { // Add optional flag
    // Keep roomCode if we want to go back to lobby
    const currentRoomCode = roomCode;
    const currentUsername = username;
    const currentBirthYear = birthYear;
    const currentLocation = location;
    const currentRole = role; // We might need this to decide next view?

    setView('home'); // Default view reset
    setRole(null);
    setInputRoomCode(''); // Clear input field
    setRoomCode(null); // Clear current room
    setIsHost(false); setLobbyPlayers([]); setHostUsername(''); setCurrentQuestion(null);
    setPlayerAnswerResult(null); setDisplayRevealData(null); setFinalLeaderboard(null);
    setError(null); setSubmittedAnswerIndex(null); setTimeRemaining(0); setAnswerCount({ count: 0, total: 0 });
    // Keep demographics? Or reset? Let's reset for now unless going back to lobby
    setBirthYear('1990'); setLocation('Sweden'); setQuestionLimit(10); // Reset limit too
    setCurrentAttemptId(null); setRatingInput('5'); setSubmittedRating(null);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

    // --- NEW: Logic to return to lobby ---
    if (!backToHome && currentRoomCode) {
        // If not explicitly going home AND we were in a room, rejoin lobby state
        setRoomCode(currentRoomCode); // Restore room code
        setUsername(currentUsername); // Restore user details
        setBirthYear(currentBirthYear);
        setLocation(currentLocation);
        setRole(currentRole); // Restore role
        // We need lobby data again, backend should handle this ideally
        // upon ending or via a 'play_again' event.
        // For now, just set the view. Backend needs adjustment if full reset needed.
        // Let's simplify: button just calls this, and it goes to home.
        // A "Play Again" button would ideally trigger a backend reset event.
        // **Let's revert this complexity for now and just change the button text.**
        // The user just goes back home and can rejoin/create.
        // Simpler implementation: Reset everything, change button text.
    }
     // If backToHome is true, the defaults above will apply, sending user home.

  }, [roomCode, username, birthYear, location, role]); // Add dependencies

  // --- Socket Event Handlers ---
  useEffect(() => {
    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => { setIsConnected(false); setError("Disconnected."); resetGameState(); };
    const handleError = (data) => setError(data?.message || 'Unknown server error.');
    const handleJoinError = (data) => setError(data?.message || 'Failed to join.');

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('error', handleError);
    socket.on('join_error', handleJoinError);

    socket.on('game_created', (data) => {
      clearError(); setRoomCode(data.room_code); setUsername(data.username);
      setRole('host'); setIsHost(true); setView('lobby'); // Host is now also a player role conceptually
    });

    socket.on('join_success', (data) => {
      clearError(); setRoomCode(data.room_code); setRole(data.role); // 'player' or 'display'
      if(data.role === 'player') setUsername(data.username); // Only set username if joining as player
      setIsHost(false); // Only creator is host
      setView('lobby');
    });

    socket.on('lobby_update', (data) => {
      setLobbyPlayers(data.players || []);
      setHostUsername(data.host_username || 'Host'); // Get host's name
    });

    socket.on('new_question', (data) => {
      setCurrentQuestion(data); setPlayerAnswerResult(null); setDisplayRevealData(null);
      setSubmittedAnswerIndex(null); setError(null);
      setQuestionStartTime(data.question_start_time || null);
      // Calculate initial time remaining based on backend timestamp
      if (data.question_start_time && data.time_limit) {
        const now = Date.now() / 1000;
        const remaining = Math.max(0, Math.round(data.question_start_time + data.time_limit - now));
        setTimeRemaining(remaining);
      } else {
        setTimeRemaining(data.time_limit || 15);
      }
      setAnswerCount({ count: 0, total: lobbyPlayers.length });
      // NEW: Reset rating state for new question
      setCurrentAttemptId(null); setRatingInput('5'); setSubmittedRating(null);
      setView('question');
    });

    // Listener for player answer count updates (for Host/Display)
    socket.on('player_answered', (data) => {
        if (role === 'host' || role === 'display') {
            setAnswerCount({ count: data.count, total: data.total });
        }
    });

    socket.on('answer_result', (data) => { // Only for players (incl host)
      if (role === 'host' || role === 'player') {
          setPlayerAnswerResult(data);
          // NEW: Store the attempt_id for rating submission
          if (data.attempt_id) {
              setCurrentAttemptId(data.attempt_id);
              console.log("Received attempt_id:", data.attempt_id); // Debug log
          } else {
              console.warn("Did not receive attempt_id in answer_result");
          }
          if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
          setView('reveal');
      }
    });

     socket.on('reveal_answer', (data) => { // Only for display (and host acts like display here too)
       if (role === 'host' || role === 'display') {
           setDisplayRevealData(data);
           if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
           setView('reveal');
       }
    });

    socket.on('game_over', (data) => {
      setFinalLeaderboard(data.leaderboard); setCurrentQuestion(null);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      setView('end');
    });

     socket.on('game_reset', (data) => {
        setError(data.message || "Game reset."); setCurrentQuestion(null);
        setPlayerAnswerResult(null); setDisplayRevealData(null); setFinalLeaderboard(null);
        setSubmittedAnswerIndex(null); setTimeRemaining(0); setAnswerCount({ count: 0, total: 0 });
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        setView('lobby');
     });

    // NEW: Handle rating confirmation (optional)
    socket.on('rating_received', (data) => {
        if (data.attempt_id === currentAttemptId) {
            console.log(`Rating ${data.rating} confirmed for attempt ${data.attempt_id}`);
            // Optionally show a small confirmation message
        }
    });

    return () => { // Cleanup
      socket.off('connect', handleConnect); socket.off('disconnect', handleDisconnect);
      socket.off('error', handleError); socket.off('join_error', handleJoinError);
      socket.off('game_created'); socket.off('join_success'); socket.off('lobby_update');
      socket.off('new_question'); socket.off('player_answered'); socket.off('answer_result');
      socket.off('reveal_answer'); socket.off('game_over'); socket.off('game_reset');
      socket.off('rating_received');
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [resetGameState, role, lobbyPlayers.length, currentAttemptId]); // Added currentAttemptId dependency

  // --- Timer Effect (Unchanged) ---
  useEffect(() => {
    if (view === 'question' && questionStartTime && currentQuestion?.time_limit) {
      // Immediately set the correct time remaining
      const updateTime = () => {
        const now = Date.now() / 1000;
        const remaining = Math.max(0, Math.round(questionStartTime + currentQuestion.time_limit - now));
        setTimeRemaining(remaining);
        if (remaining <= 0 && timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
      updateTime();
      timerIntervalRef.current = setInterval(updateTime, 1000);
    } else if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
  }, [view, questionStartTime, currentQuestion]);

  // Add URL parameter handling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomFromUrl = params.get('room');
    if (roomFromUrl) {
      setInputRoomCode(roomFromUrl);
      // Clear the URL parameters without refreshing the page
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // --- Fetch sets from backend when entering lobby as host ---
  useEffect(() => {
    if (isHost && view === 'lobby') {
      console.log('Fetching sets from backend...');
      fetch(`${SOCKET_URL}/api/quiz_sets`)
        .then(res => res.json())
        .then(data => {
          console.log('Fetched sets:', data.sets);
          setAvailableSets(data.sets || []);
        })
        .catch(() => setAvailableSets([]));
    }
  }, [isHost, view]);

  // --- Action Handlers ---
  const handleCreateGame = useCallback(() => {
    clearError(); if (!username.trim()) { setError("Username required."); return; }
    socket.emit('create_game', { username, host_as_player: hostAsPlayer });
  }, [username, hostAsPlayer]);

  const handleJoinAction = useCallback((joinRole) => {
      clearError();
      const code = inputRoomCode.trim().toUpperCase();
      if (!code) { setError("Room code required."); return; }
      if (joinRole === 'player' && !username.trim()) { setError("Username required for players."); return; }

      // Birth year is already a string from the slider state
      let yearToSend = null;
      if (joinRole === 'player' && birthYear) {
        yearToSend = parseInt(birthYear, 10); // Convert slider string value to number
        // Optional: Add validation here if slider wasn't enough, though unlikely
      }

      setInputRoomCode(code);
      socket.emit('join_game', {
          room_code: code,
          username: joinRole === 'player' ? username : undefined,
          role: joinRole,
          // Send current state values
          birth_year: joinRole === 'player' ? yearToSend : undefined,
          location: joinRole === 'player' ? location : undefined // Location is directly from state
      });
  }, [username, inputRoomCode, birthYear, location]);


  const handleStartGame = useCallback(() => {
    clearError();
    if (isHost && roomCode) {
      socket.emit('start_game', {
        room_code: roomCode,
        question_limit: questionLimit,
        set_id: selectedSetId || undefined // Send set_id if selected
      });
    }
  }, [isHost, roomCode, questionLimit, selectedSetId]);

  const handleAnswerSubmit = useCallback((index) => {
     // Only players (including host) can submit, and only once
     if (submittedAnswerIndex !== null || !roomCode || (role !== 'player' && role !== 'host')) return;
     clearError(); setSubmittedAnswerIndex(index);
     socket.emit('submit_answer', { room_code: roomCode, answer_index: index });
  }, [submittedAnswerIndex, roomCode, role]);

  // NEW: Handle Rating Submission
  const handleRatingSubmit = useCallback(() => {
    if (submittedRating !== null || currentAttemptId === null || (role !== 'player' && role !== 'host')) return;
    clearError();
    const ratingValue = parseFloat(ratingInput); // ratingInput is string from slider state
    if (isNaN(ratingValue) || ratingValue < 0 || ratingValue > 10) {
        setError("Please enter a valid rating between 0 and 10.");
        return;
    }
    console.log(`Submitting rating ${ratingValue} for attempt ${currentAttemptId}`); // Debug log
    socket.emit('submit_rating', {
        room_code: roomCode,
        attempt_id: currentAttemptId,
        rating: ratingValue
    });
    setSubmittedRating(ratingValue); // Mark as submitted for this question
  }, [submittedRating, currentAttemptId, ratingInput, roomCode, role]);

  // --- Render Logic ---

  const renderHomeScreen = () => (
    <div className="screen home-screen">
      <h2>Welcome to QUPPLO Quiz!</h2>
      <input type="text" placeholder="Enter Your Username" value={username} onChange={(e) => setUsername(e.target.value)} maxLength={20} />

      {/* --- Host Mode Toggle --- */}
      <div className="host-mode-toggle">
        <label>
          <input
            type="checkbox"
            checked={hostAsPlayer}
            onChange={e => setHostAsPlayer(e.target.checked)}
          />
          Participate as player (host answers questions)
        </label>
        <div className="host-mode-desc">
          <small>
            If unchecked, the host will only control the game and act as the display, but will not participate as a player.
          </small>
        </div>
      </div>
      {/* --- End Host Mode Toggle --- */}

      {/* --- Updated Demographics Inputs --- */}
      <div className="demographics-inputs">
          {/* Birth Year Slider */}
          <div className="slider-container">
              <label htmlFor="birthYearSlider">Birth Year: <span className="slider-value">{birthYear}</span></label>
              <input
                  type="range"
                  id="birthYearSlider"
                  min="1910"
                  max="2025" // Or current year dynamically
                  step="1"
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  className="slider-input"
              />
          </div>

          {/* Location Dropdown */}
          <div className="select-container">
              <label htmlFor="locationSelect">Location:</label>
              <select
                  id="locationSelect"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="select-input"
              >
                  {locationOptions.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                  ))}
              </select>
          </div>
      </div>
      {/* --- End Updated --- */}

      <div className="actions create-action">
        <button onClick={handleCreateGame} disabled={!isConnected || !username.trim()}>Create Game (as Host)</button>
      </div>
      <div className="actions join-actions">
        <input
            type="text"
            placeholder="Enter Room Code (4 chars)"
            value={inputRoomCode}
            onChange={(e) => setInputRoomCode(e.target.value.toUpperCase().slice(0, 4))}  // Limit to 4 chars
            maxLength={4}
            style={{ textTransform: 'uppercase' }}
        />
        <button onClick={() => handleJoinAction('player')} disabled={!isConnected || !username.trim() || !inputRoomCode.trim()}>Join as Player</button>
        <button onClick={() => handleJoinAction('display')} disabled={!isConnected || !inputRoomCode.trim()}>Join as Display</button>
      </div>
    </div>
  );

  const renderLobbyScreen = () => {
    const baseUrl = window.location.origin;
    const joinUrl = `${baseUrl}?room=${roomCode}`;

    return (
      <div className="screen lobby-screen">
        <h2>Lobby - Room: <span className="room-code">{roomCode}</span></h2>
        <p>You are: <span className="role">{role === 'host' ? `Host (${username})` : role}</span></p>
        {role !== 'display' && <p>Host: {hostUsername}</p>}

        <div className="qr-section">
            <QRCodeSVG value={joinUrl} size={128} />
            <p className="qr-hint">Scan or share link to join</p>
            {/* Optionally display the URL */}
            {/* <p className="join-url">Join at: <a href={joinUrl} target="_blank" rel="noopener noreferrer">{joinUrl}</a></p> */}
        </div>

        {/* --- NEW: Question Limit Slider for Host --- */}
        {isHost && (
            <div className="lobby-settings">
                <h4>Game Settings</h4>
                <div className="set-select-container">
                    <label htmlFor="setSelect">Question Set:</label>
                    <select
                        id="setSelect"
                        value={selectedSetId}
                        onChange={e => setSelectedSetId(e.target.value)}
                    >
                        <option value="">-- None (random questions) --</option>
                        {availableSets.map(set => (
                            <option key={set.set_id} value={set.set_id}>
                                {set.set_name} {set.question_count !== undefined ? `(${set.question_count} questions)` : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="slider-container">
                    <label htmlFor="questionLimitSlider">Number of Questions: <span className="slider-value">{questionLimit}</span></label>
                    <input
                        type="range"
                        id="questionLimitSlider"
                        min="1"
                        max="100"
                        step="1"
                        value={questionLimit}
                        onChange={e => setQuestionLimit(parseInt(e.target.value, 10))}
                        className="slider-input"
                        disabled={!!selectedSetId} // Disable if set selected
                    />
                </div>
            </div>
        )}
        {/* --- End NEW --- */}

        <h3>Players ({lobbyPlayers.length}):</h3>
        <ul className="player-list">
          {lobbyPlayers.map((name, index) => (
            <li key={index}>{name}{name === username ? ' (You)' : ''}</li>
          ))}
        </ul>

        {/* Show Start Game button for host if: (A) host is a player and there is at least 1 player, OR (B) host is display-only (non-playing) regardless of player count */}
        {isHost && ((hostAsPlayer && lobbyPlayers.length >= 1) || !hostAsPlayer) && (
          <button onClick={handleStartGame} className="start-button">Start Game</button>
        )}
        {isHost && hostAsPlayer && lobbyPlayers.length < 1 && (<p>Waiting for players...</p>)}
        {!isHost && role === 'player' && (<p>Waiting for host ({hostUsername}) to start...</p>)}
        {role === 'display' && (<p>Waiting for host ({hostUsername}) to start the game...</p>)}
      </div>
    );
  };

 const renderQuestionScreen = () => {
    if (!currentQuestion) return <div className="screen loading">Waiting for question...</div>;
    const showFullDetails = (role === 'host' || role === 'display'); // Host & Display see full details
    const questionContainerClass = `screen question-screen ${view === 'question' ? 'visible' : ''}`;

    return (
      <div className={questionContainerClass}>
        <div className="game-info-bar">
             <span className="q-number">Q: {currentQuestion.index + 1} / {currentQuestion.totalQuestions || '?'}</span> {/* Add total Q count if available */}
             {showFullDetails && <span className="answer-count">{answerCount.count} / {answerCount.total} answered</span>}
             <span className="timer">Time: {timeRemaining}</span>
        </div>
        <div className="question-box">
            <p className="q-text">{currentQuestion.text}</p>
          </div>

        <div className={`question-layout${currentQuestion.num_alternatives === 4 ? ' four-alts' : ''}`}>
          {/* --- Question Box --- */}

          {/* --- Answer Alternatives --- */}
          {Array.from({ length: currentQuestion.num_alternatives }).map((_, index) => {
            const alt = currentQuestion.alts[index];
            // Convert index to letter (A, B, C, ...)
            const altLabel = String.fromCharCode(65 + index);
            // Player/Host sees buttons, Display sees static boxes
            return (role === 'player' || role === 'host')
              ? ( /* Player/Host Button */
                <button
                  key={index}
                  className={`answer-box answer-button answer-${index % 4} ${submittedAnswerIndex === index ? 'submitted' : ''}`}
                  onClick={() => handleAnswerSubmit(index)}
                  disabled={submittedAnswerIndex !== null || role === 'display'}
                >
                   <span className="alt-shape">{altLabel}</span>
                   {alt?.image ? (
                     <img src={SOCKET_URL + alt.image} alt={`Alternative ${altLabel}`} className="alt-image" />
                   ) : (
                     <span className="alt-text">{alt?.text}</span>
                   )}
                </button>
              )
              : ( /* Display Static Box */
                <div key={index} className={`answer-box answer-${index % 4}`}>
                  <span className="alt-shape">{altLabel}</span>
                  {alt?.image ? (
                    <img src={SOCKET_URL + alt.image} alt={`Alternative ${altLabel}`} className="alt-image" />
                  ) : (
                    <span className="alt-text">{alt?.text}</span>
                  )}
                </div>
              );
          })}

          {/* --- Stats Box Placeholder (Optional) --- */}
          {/* Removed for simplicity, info bar is better */}
          {/* <div className="answer-box stats-box"> ... </div> */}
        </div>
      </div>
    );
  };

  const renderRevealScreen = () => {
     const isPlayerOrHost = role === 'player' || role === 'host';
     const isDisplay = role === 'display';
     const isDisplayOnlyHost = role === 'host' && hostAsPlayer === false;

     // --- Conditional Data Check (Revised) ---
     // Special case: display-only host
     if (isDisplayOnlyHost && !displayRevealData) {
         return <div className="screen loading">Waiting for results...</div>;
     }
     // Normal player/host (not display-only)
     if (!isDisplayOnlyHost && isPlayerOrHost && !playerAnswerResult) {
         // Player or Host is waiting for their specific result
         return <div className="screen loading">Waiting for your results...</div>;
     }
     if (isDisplay && !displayRevealData) {
         // Display is waiting for the overall reveal data
         return <div className="screen loading">Waiting for results...</div>;
     }
     // Host needs both for the full view (player result + display data), unless display-only
     if (role === 'host' && !isDisplayOnlyHost && (!playerAnswerResult || !displayRevealData)) {
         // Host is still waiting for one piece of data
         return <div className="screen loading">Waiting for full results...</div>;
     }
     // --- End Revised Check ---

     // --- Data extraction ---
     // Player/Host specific info comes from playerAnswerResult
     const { correct, score_added, total_score, attempt_id } = playerAnswerResult || {};

     // General reveal info (correct answer, explanation, stats, leaderboard)
     // comes from displayRevealData for Display role, or can come from either for Host/Player
     // Let's prioritize displayRevealData if available (it's the "official" reveal info)
     // Fallback to playerAnswerResult for text/index if displayRevealData is somehow missing for host/player
     const revealInfoSource = displayRevealData || playerAnswerResult; // Prioritize displayRevealData

     const { correct_index, correct_answer_text, explanation, stats, leaderboard } = revealInfoSource || {};

     // Final check - do we have a valid correct index to render?
      if (typeof correct_index === 'undefined') {
          console.error("Reveal screen render error: correct_index is undefined.", revealInfoSource);
          return <div className="screen loading">Processing results error...</div>;
      }

     // Base classes
     let screenClass = "screen reveal-screen";
     if (isPlayerOrHost && playerAnswerResult) {
        screenClass += playerAnswerResult.correct ? ' correct' : ' incorrect';
     }

     return (
       <div className={screenClass}>
         <h2>Results - Q: {currentQuestion?.index + 1}</h2>

         {isDisplayOnlyHost && (
           <div style={{ marginBottom: '1rem', color: '#888', fontStyle: 'italic' }}>
             Display-only mode: no players, answers shown for reference.
           </div>
         )}

         {currentQuestion?.text && <p className="question-text-reveal"><i>{currentQuestion.text}</i></p>}

         {/* Use derived values */}
         <p>
           Correct Answer was:
           <span className={`answer-highlight answer-${correct_index % 4}`}>
             {correct_answer_text?.image ? (
               <img src={SOCKET_URL + correct_answer_text.image} alt="Correct answer" className="alt-image" />
             ) : (
               correct_answer_text?.text || `Alt ${correct_index + 1}`
             )}
           </span>
         </p>
         {explanation && <p className="explanation"><i>{explanation}</i></p>}

         {/* Display & Host specific content (needs stats & leaderboard) */}
         {(isDisplay || role === 'host') && stats && leaderboard && ( // Check existence of stats & leaderboard
            <>
                <h3>Answer Distribution:</h3>
                <div className="stats-bars">
                    {stats.map((count, index) => (
                        <p key={index} className={index === correct_index ? 'correct-stat' : ''}>
                            Alt {index+1}: {count} player(s)
                        </p>
                    ))}
                </div>
                <h3>Top Players:</h3>
                <ol className="leaderboard-snippet">
                    {leaderboard.slice(0, 5).map((p, i) => <li key={i}>{p.username}: {p.score}</li>)}
                </ol>
            </>
         )}

         {/* Player/Host specific content (needs playerAnswerResult) */}
         {!isDisplayOnlyHost && isPlayerOrHost && playerAnswerResult && (
           <div className="player-score-feedback">
             <h3 className={correct ? 'correct-text' : 'incorrect-text'}>{correct ? "Correct!" : "Incorrect"}</h3>
             <p className="score-feedback">Score Added: +{score_added}</p>
             <p className="total-score">Your Total Score: {total_score}</p>

             {/* --- Updated Rating Section --- */}
             {attempt_id && (
                <div className="rating-section slider-container"> {/* Added slider-container class */}
                    <label htmlFor="ratingSlider">Rate this question: <span className="slider-value">{parseFloat(ratingInput).toFixed(1)}</span></label>
                    <input
                        type="range" // Changed type to range
                        id="ratingSlider"
                        min="0"
                        max="10"
                        step="0.5" // Allow half steps
                        value={ratingInput}
                        onChange={(e) => setRatingInput(e.target.value)}
                        disabled={submittedRating !== null}
                        className="slider-input" // Added class
                    />
                    <button
                        onClick={handleRatingSubmit}
                        disabled={submittedRating !== null || ratingInput === ''} // Check for empty string explicitly
                    >
                        {submittedRating !== null ? 'Rating Submitted!' : 'Submit Rating'}
                    </button>
                </div>
             )}
             {/* --- End Updated --- */}
           </div>
         )}

         <p className="waiting-next"><i>Waiting for next question or final results...</i></p>
       </div>
     );
  };


  const renderEndScreen = () => (
     <div className="screen end-screen">
       <h2>Game Over!</h2>
       <h3>Final Leaderboard:</h3>
       <ol className="final-leaderboard">
         {finalLeaderboard?.map((p, i) => (
           <li key={i}><span>{i+1}. {p.username}</span> <span>{p.score}</span></li>
         ))}
       </ol>
       {/* Updated Button Text and potentially onClick */}
       <button onClick={() => resetGameState(true)}>Back to Home</button> {/* Explicitly go home */}
       {/* Alternative: Add a Play Again button that emits event to backend? */}
       {/* {isHost && <button onClick={handlePlayAgain}>Play Again with Same Players</button>} */}
     </div>
   );

  // Add this debug panel component
  const DebugPanel = ({ onViewChange, currentView }) => {
    if (!DEBUG_MODE) return null;

    return (
      <div style={{
        position: 'fixed',
        bottom: '1rem',
        left: '1rem',
        padding: '1rem',
        background: 'rgba(0,0,0,0.8)',
        color: 'white',
        borderRadius: '0.5rem',
        zIndex: 1000
      }}>
        <h4 style={{marginBottom: '0.5rem'}}>Debug Controls</h4>
        <div style={{display: 'flex', gap: '0.5rem'}}>
          <button onClick={() => onViewChange('home')}>Home</button>
          <button onClick={() => onViewChange('lobby')}>Lobby</button>
          <button onClick={() => {
            onViewChange('question');
            setCurrentQuestion(DUMMY_DATA.question);
            setTimeRemaining(15);
          }}>Question</button>
          <button onClick={() => {
            onViewChange('reveal');
            setCurrentQuestion(DUMMY_DATA.question); // Ensure question is set for reveal debug
            setPlayerAnswerResult(DUMMY_DATA.playerResult);
            setDisplayRevealData(DUMMY_DATA.displayData);
          }}>Reveal</button>
          <button onClick={() => {
            onViewChange('end');
            setFinalLeaderboard(DUMMY_DATA.finalLeaderboard);
          }}>End</button>
        </div>
        <div style={{marginTop: '0.5rem'}}>
          <label>
            <input
              type="checkbox"
              checked={role === 'display'}
              onChange={(e) => setRole(e.target.checked ? 'display' : 'player')}
            /> Display Mode
          </label>
        </div>
        <div style={{fontSize: '0.8rem', marginTop: '0.5rem'}}>
          Current View: {currentView}
        </div>
      </div>
    );
  };

  // --- Main Render ---
  return (
    <div className="App">
       <header className="App-header">
        <h1>QUPPLO Quiz</h1>
        <div className="connection-status">
          Status: {isConnected ? <span className="connected">Connected</span> : <span className="disconnected">Disconnected</span>}
          {roomCode && <span className="room-info"> | Room: {roomCode}</span>}
          {username && role !== 'display' && <span className="room-info"> | User: {username}</span>}
           {role === 'display' && <span className="room-info"> | Role: Display</span>}
        </div>
      </header>
      <main className="App-content">
        {error && <div className="error-message" onClick={clearError}>Error: {error} (click to dismiss)</div>}
        {view === 'home' && renderHomeScreen()}
        {view === 'lobby' && renderLobbyScreen()}
        {view === 'question' && renderQuestionScreen()}
        {view === 'reveal' && renderRevealScreen()}
        {view === 'end' && renderEndScreen()}
      </main>
      <DebugPanel onViewChange={setView} currentView={view} />
    </div>
  );
}

export default App;